/**
 * Disziplin Langstrecke: kein Ziel, kein Limit. Gescrollt wird, bis der Daumen
 * aufgibt.
 *
 * Damit das nicht ins Leere laeuft, blendet die Bahn immer die naechste runde
 * Marke ein - 10 m, 100 m, 1 km. Ein Ziel in Sichtweite haelt laenger bei der
 * Sache als eine Zahl, die einfach hochzaehlt.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { Header } from '../components/Header'
import { LiveValue } from '../components/LiveValue'
import { ResultPanel } from '../components/ResultPanel'
import { ScrollTrack } from '../components/ScrollTrack'
import type { TapeMarker } from '../components/drawTape'
import { useDevice } from '../DeviceContext'
import { LongDistanceRun } from '../engine/disciplines'
import { loadRecords } from '../engine/records'
import type { ScrollTracker } from '../engine/scrollTracker'
import { formatDistance, formatDistanceInline, formatSpeed, formatTime } from '../engine/units'
import type { DisciplineChallengeProps } from './Sprung'

/** Etappenziele in Zentimetern: 1 m bis 42,195 km. */
const MILESTONES_CM = [
  100, 500, 1_000, 5_000, 10_000, 25_000, 50_000, 100_000, 200_000, 500_000, 1_000_000, 2_000_000,
  4_219_500,
]

const MILESTONE_LABELS: Record<number, string> = {
  4_219_500: 'Marathon',
}

export function LangstreckeScreen({
  challengeCode,
  rivalCm,
  rivalName,
  onAnswered,
}: DisciplineChallengeProps = {}) {
  const { metrics } = useDevice()
  const trackerRef = useRef<ScrollTracker | null>(null)
  const [run, setRun] = useState<LongDistanceRun | null>(null)
  const [finished, setFinished] = useState(false)
  const [, rerender] = useReducer((count: number) => count + 1, 0)

  const handleTracker = useCallback(
    (tracker: ScrollTracker) => {
      trackerRef.current = tracker
      setRun(new LongDistanceRun(tracker, rerender))
    },
    [rerender],
  )

  // Die grossen Zahlen laufen ueber LiveValue pro Bild. Dieser Takt dient nur
  // den selten wechselnden Teilen: Etappenziel und Zwischenwerte.
  useEffect(() => {
    const id = window.setInterval(rerender, 500)
    return () => window.clearInterval(id)
  }, [rerender])

  const liveDistance = useCallback(() => {
    const formatted = formatDistance(trackerRef.current?.forwardCm ?? 0)
    return `${formatted.value} ${formatted.unit}`
  }, [])

  const liveSpeed = useCallback(() => formatSpeed(trackerRef.current?.velocityCmPerSec ?? 0), [])

  const distanceCm = run?.distanceCm ?? 0
  const markers: TapeMarker[] = []
  if (rivalCm && rivalCm > 0) {
    markers.push({ cm: rivalCm, label: `${rivalName ?? 'Gegner'} ${formatDistanceInline(rivalCm)}` })
  }
  const nextMilestone = MILESTONES_CM.find((cm) => cm > distanceCm)
  if (nextMilestone) {
    markers.push({
      cm: nextMilestone,
      label: MILESTONE_LABELS[nextMilestone] ?? formatDistanceInline(nextMilestone),
    })
  }

  const lifetimeCm = loadRecords().lifetimeCm

  return (
    <div className="screen" data-discipline="langstrecke">
      <Header title="Langstrecke" />
      <ScrollTrack metrics={metrics} onTracker={handleTracker} markers={markers} referenceRatio={0.3}>
        {!finished && (
          <div className="hud hud--center">
            <LiveValue get={liveDistance} className="hud__value" />
            <p className="hud__hint">
              <LiveValue get={liveSpeed} /> · {formatTime(run?.activeTimeMs ?? 0)} s aktiv
            </p>
            {!run?.hasStarted && <p className="hud__note">Nach oben wischen. Rückwärts zählt nicht.</p>}
            <p className="hud__note hud__note--dim">
              Lebensleistung {formatDistanceInline(lifetimeCm)}
            </p>
          </div>
        )}

        {finished && run && (
          <ResultPanel
            result={run.toResult()}
            headline={formatDistanceInline(run.distanceCm)}
            subline={`${formatTime(run.activeTimeMs)} s aktiv · Ø ${formatSpeed(run.averageSpeedCmPerSec)}`}
            challengeCode={challengeCode}
            onChallengeAnswered={onAnswered}
            onRetry={() => {
              run.reset()
              setFinished(false)
            }}
          />
        )}
      </ScrollTrack>

      {!finished && run?.hasStarted && (
        <button className="track__leave" type="button" onClick={() => setFinished(true)}>
          Lauf beenden
        </button>
      )}
    </div>
  )
}
